/**
 * Editor Integration — the session contract (data).
 *
 * The bridge between the AI Design Engine and the iVisora editor. Everything
 * here describes what the editor expects; the editor itself is never changed,
 * and no conversion is left for it to do.
 */

export const SESSION_SCHEMA_ID = 'editorsession/1';
export const SESSION_VERSION = 1;

/** The element types the editor renders natively. */
export const EDITOR_ELEMENT_TYPES = Object.freeze(['text', 'shape', 'image', 'frame', 'sticker', 'group']);

/** Every element carries these; the editor reads them directly. */
export const ELEMENT_BASE_FIELDS = Object.freeze(['id', 'type', 'x', 'y', 'width', 'height', 'rotation', 'opacity', 'visible', 'locked', 'parentId']);

/** Every page carries these. */
export const PAGE_FIELDS = Object.freeze(['id', 'name', 'elements', 'background', 'visible', 'locked']);

/** Types with no geometry of their own — the editor derives their bounds. */
export const GEOMETRYLESS_TYPES = Object.freeze(['group']);

/** The editor's own defaults, mirrored so a session never arrives half-formed. */
export const EDITOR_DEFAULTS = Object.freeze({
  background: '#ffffff',
  canvas: { width: 1080, height: 1080 },
  pageName: 'Page 1',
  designName: 'Untitled Design',
  rotation: 0,
  opacity: 1,
});

/** The editor's autosave contract (EditorPage owns the writing; we only prepare it). */
export const AUTOSAVE = Object.freeze({
  newDesignKey: 'ivisora_autosave_new',
  keyPrefix: 'ivisora_autosave_',
  debounceMs: 2000,
});

/** How many steps of history a session starts with room for. */
export const HISTORY_LIMIT = 50;

/** Analytics events this layer emits descriptors for. It never sends them. */
export const ANALYTICS_EVENTS = Object.freeze({
  SESSION_CREATED: 'ai_editor_session_created',
  SESSION_REJECTED: 'ai_editor_session_rejected',
});

/**
 * Capabilities a session declares. The future ones are real seams: the session
 * carries the identity and hooks they need, and nothing switches them on today.
 */
export const CAPABILITIES = Object.freeze(['collaboration', 'comments', 'versionHistory', 'assetCdn']);

/** Findings are advisory or blocking, never thrown. */
export const SEVERITY = Object.freeze({ ERROR: 'error', WARNING: 'warning' });

/** Where the renderer parked everything the editor has no native slot for. */
export const META_NAMESPACE = 'designV2';
